
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface QuotaContextType {
    isQuotaExceeded: boolean;
    triggerQuotaExceeded: () => void;
    resetQuota: () => void;
}

const QuotaContext = createContext<QuotaContextType | undefined>(undefined);

export function QuotaProvider({ children }: { children: ReactNode }) {
    const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);

    const triggerQuotaExceeded = () => setIsQuotaExceeded(true);
    const resetQuota = () => setIsQuotaExceeded(false);

    useEffect(() => {
        const originalFetch = window.fetch;

        window.fetch = async (...args) => {
            try {
                const response = await originalFetch(...args);

                // Cloudflare often returns 503 or 1015/1027 (as 530) for rate limits
                // We catch 503 (Service Unavailable) and 429 (Too Many Requests)
                // We also check for specific Cloudflare headers if needed, but status is usually enough
                if (response.status === 503 || response.status === 429) {
                    console.warn("Global Interceptor: Quota/Rate Limit detected (Status " + response.status + ")");
                    setIsQuotaExceeded(true);
                }

                return response;
            } catch (error) {
                // Network errors (DNS, offline) usually throw here
                // We don't necessarily want to trigger Quota for 'Offline', but we could.
                // For now, let's stick to Server Response codes.
                throw error;
            }
        };

        return () => {
            window.fetch = originalFetch;
        };
    }, []);

    return (
        <QuotaContext.Provider value={{ isQuotaExceeded, triggerQuotaExceeded, resetQuota }}>
            {children}
        </QuotaContext.Provider>
    );
}

export function useQuota() {
    const context = useContext(QuotaContext);
    if (context === undefined) {
        throw new Error('useQuota must be used within a QuotaProvider');
    }
    return context;
}
